// src/pages/programs/ShowProgram.jsx
import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, Container, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import GlobalBreadcrumbs from "../../components/global/GlobalBreadcrumbs";
import useGetProgram from "../../hooks/programs/useGetProgram";
import LoadingPage from "../../components/global/LoadingPage";
import WrapperSection from "../../components/global/WrapperSection";
import ProgramActions from "../../components/programs/ShowProgram/ProgramActions";
import ProgramHeader from "../../components/programs/ShowProgram/ProgramHeader";
import ProgramOverview from "../../components/programs/ShowProgram/ProgramOverview";
import ProgramDayCard from "../../components/programs/ShowProgram/ProgramDayCard";
import ProgramPricing from "../../components/programs/ShowProgram/ProgramPricing";

import "../../styles/PrintStyles.css";
import PrintProgram from "../../components/programs/ShowProgram/PrintProgram";
import useConfirmProgram from "../../hooks/programs/useConfirmProgram";

const ShowProgram = () => {
    const { id } = useParams();
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng === "ar";

    const { program, isLoading, error } = useGetProgram(id);
    const { confirmProgramMutate, isPending: isConfirming } = useConfirmProgram();

    const enrichedDays = useMemo(() => {
        if (!program?.days) return [];
        const days = program.days.map(d => ({ ...d, products: [...(d.products || [])] }));
        days.forEach((currentDay, idx) => {
            const hotelProduct = currentDay.products.find(p => p.hotel_details);
            if (!hotelProduct) return;
            const nights = parseInt(hotelProduct.nights) || 1;
            for (let i = 1; i < nights && idx + i < days.length; i++) {
                const targetDay = days[idx + i];
                if (!targetDay.products.some(p => p.hotel_details && p.id === hotelProduct.id)) {
                    targetDay.products.push({ ...hotelProduct, _dayKey: `${hotelProduct.id}_${targetDay.date || idx + i}` });
                }
            }
        });
        return days;
    }, [program?.days]);

    // Set document title for PDF export filename
    React.useEffect(() => {
        if (program?.title) {
            document.title = `${program.title[lng]}`;
        }
    }, [program, lng]);

    if (isLoading) {
        return <LoadingPage />;
    }

    if (error) {
        return (
            <Container maxWidth="xl">
                <Typography color="error" sx={{ mt: 4 }}>
                    {isArabic ? "حدث خطأ أثناء تحميل البرنامج" : "Error loading program"}
                </Typography>
            </Container>
        );
    }

    return (
        <WrapperSection>
            {/* ========== العناصر المخفية عند الطباعة ========== */}
            <div className="no-print">
                <GlobalBreadcrumbs />
            </div>

            <div className="no-print">
                <ProgramActions program={program} confirmProgramMutate={confirmProgramMutate} isConfirming={isConfirming} />
            </div>

            {/* ========== نسخة الطباعة - تظهر فقط عند Print ========== */}
            <div className="print-only">
                <PrintProgram program={program} lang={lng} />
            </div>

            {/* ========== المحتوى للعرض العادي والطباعة ========== */}
            <Box id="program-content">
                <ProgramHeader program={program} />
                <ProgramOverview program={program} confirmProgramMutate={confirmProgramMutate} isConfirming={isConfirming}/>

                <Box className="days-list" sx={{ 
                    mt: 4,
                    '@media print': {
                        pageBreakBefore: 'auto',
                        mt: 1
                    }
                }}>
                    {enrichedDays.map((day, index) => (
                        <ProgramDayCard
                            key={day.id}
                            day={day}
                            dayNumber={index + 1}
                            totalDays={enrichedDays.length}
                        />
                    ))}
                </Box>

                <ProgramPricing program={program} />
            </Box>
        </WrapperSection>
    );
};

export default ShowProgram;
